

import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Loader2, Gift, Check, Package } from 'lucide-react';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalFooter } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { AddressAutocomplete, isPlacesDropdownVisible } from '@/components/ui/address-autocomplete';
import { useHaptics } from '@/hooks/useHaptics';
import { getAccessToken } from '@/lib/auth-client';
import { claimEmailPrefill, isValidClaimEmail } from '@/lib/claim-email';
import { API_BASE } from '@/lib/api';

interface PrizeClaim {
  id: string;
  mainEventId: string;
  itemName: string | null;
  itemImageUrl: string | null;
  rank: number;
}

interface ClaimPrizeModalProps {
  claim: PrizeClaim | null;
  userEmail: string;
  isOpen: boolean;
  onClose: () => void;
  onClaimed: () => void;
}

const inputClass =
  'w-full px-3 py-2 rounded-lg bg-[#1C1917] border border-[#33302B] text-[#F5F0E8] placeholder-[#7D7568] text-sm focus:border-[#B8A04C] focus:outline-none';

export function ClaimPrizeModal({ claim, userEmail, isOpen, onClose, onClaimed }: ClaimPrizeModalProps) {
  const [form, setForm] = useState({
    shippingName: '',
    shippingEmail: claimEmailPrefill(userEmail),
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const reducedMotion = useReducedMotion();
  const { fire } = useHaptics();

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  }

  // The success state is the payoff for a physical prize, so it persists until
  // the user dismisses it. Only a post-claim close clears the form — cancelling
  // mid-address keeps what was typed, as it always has.
  function handleClose() {
    onClose();
    if (!success) return;
    setSuccess(false);
    setForm((prev) => ({ ...prev, shippingName: '', addressLine1: '', addressLine2: '', city: '', state: '', zip: '', phone: '' }));
  }

  async function handleSubmit() {
    if (!claim) return;

    const { shippingName, shippingEmail, addressLine1, city, state, zip, country } = form;
    if (!shippingName || !shippingEmail || !addressLine1 || !city || !state || !zip || !country) {
      setError('Please fill in all required fields');
      return;
    }
    if (!isValidClaimEmail(shippingEmail)) {
      setError('Enter a real email so we can contact you about your prize');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = getAccessToken();
      const res = await fetch(`${API_BASE}/api/main-events/${claim.mainEventId}/claim-prize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ claimId: claim.id, shipping: form }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to claim prize');
      }

      setSuccess(true);
      fire('success');
      onClaimed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to claim prize');
    } finally {
      setSubmitting(false);
    }
  }

  if (!claim) return null;

  return (
    <Modal open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <ModalContent className="max-w-md" onPointerDownOutside={(e) => {
        // Prevent Radix from closing modal when Google Places dropdown is visible
        if (isPlacesDropdownVisible()) e.preventDefault();
      }}>
        <ModalHeader>
          <ModalTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-[#B8A04C]" />
            Claim Your Prize
          </ModalTitle>
          <ModalDescription>
            {claim.itemName} — Rank #{claim.rank}
          </ModalDescription>
        </ModalHeader>

        {success ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <div className="relative">
              {claim.itemImageUrl ? (
                <img
                  src={claim.itemImageUrl}
                  alt={claim.itemName ?? 'Your prize'}
                  className="w-36 h-36 rounded-xl object-cover border border-[#B8A04C]/40"
                />
              ) : (
                <div className="w-36 h-36 rounded-xl bg-[#1C1917] border border-[#33302B] flex items-center justify-center">
                  <Gift className="h-12 w-12 text-[#B8A04C]" />
                </div>
              )}
              <motion.div
                initial={reducedMotion ? false : { scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={reducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 260, damping: 20 }}
                className="absolute -bottom-2 -right-2 w-11 h-11 rounded-full bg-[#272320] border-2 border-[#5B8A72]/40 flex items-center justify-center"
              >
                <Check className="h-6 w-6 text-[#5B8A72]" />
              </motion.div>
            </div>
            <p className="text-[#F5F0E8] font-medium">Prize claimed!</p>
            {claim.itemName && (
              <p className="font-serif font-bold text-[#B8A04C]">{claim.itemName}</p>
            )}
            <p className="text-sm text-[#B8B0A0] flex items-center justify-center gap-1.5">
              <Package className="h-3.5 w-3.5 text-[#B8A04C] shrink-0" />
              Processing — we&apos;ll notify you when it ships.
            </p>
            <div className="mt-2">
              <Button variant="coins" onClick={handleClose}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {error && (
                <p className="text-sm text-[#8B3A3A] bg-[#8B3A3A]/10 px-3 py-2 rounded-lg">{error}</p>
              )}

              <div>
                <label className="block text-xs text-[#B8B0A0] mb-1">Full Name *</label>
                <input name="shippingName" value={form.shippingName} onChange={handleChange} placeholder="John Doe" className={inputClass} />
              </div>

              <div>
                <label className="block text-xs text-[#B8B0A0] mb-1">Email *</label>
                <input name="shippingEmail" value={form.shippingEmail} onChange={handleChange} type="email" placeholder="you@example.com" className={inputClass} />
                <p className="text-[11px] text-[#7D7568] mt-1">We&apos;ll use this to contact you about your prize.</p>
              </div>

              <div>
                <label className="block text-xs text-[#B8B0A0] mb-1">Address Line 1 *</label>
                <AddressAutocomplete
                  value={form.addressLine1}
                  onChange={(val) => { setForm((prev) => ({ ...prev, addressLine1: val })); setError(''); }}
                  onSelect={(addr) => {
                    setForm((prev) => ({
                      ...prev,
                      addressLine1: addr.addressLine1,
                      city: addr.city,
                      state: addr.state,
                      zip: addr.zip,
                      country: addr.country,
                    }));
                    setError('');
                  }}
                  placeholder="Start typing an address..."
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs text-[#B8B0A0] mb-1">Address Line 2</label>
                <input name="addressLine2" value={form.addressLine2} onChange={handleChange} placeholder="Apt 4B" className={inputClass} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#B8B0A0] mb-1">City *</label>
                  <input name="city" value={form.city} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs text-[#B8B0A0] mb-1">State *</label>
                  <input name="state" value={form.state} onChange={handleChange} className={inputClass} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#B8B0A0] mb-1">ZIP Code *</label>
                  <input name="zip" value={form.zip} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs text-[#B8B0A0] mb-1">Country *</label>
                  <input name="country" value={form.country} onChange={handleChange} className={inputClass} />
                </div>
              </div>

              <div>
                <label className="block text-xs text-[#B8B0A0] mb-1">Phone (optional)</label>
                <input name="phone" value={form.phone} onChange={handleChange} placeholder="+1 555-123-4567" className={inputClass} />
              </div>
            </div>

            <ModalFooter className="mt-4">
              <Button variant="ghost" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="coins" onClick={handleSubmit} disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Submit Claim
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
